import { useEffect, useState } from "react";

/** Rows a build can ask for; the account's max (from the quota) cuts the list. */
export const ROW_CHOICES = [25, 50, 100, 200, 500, 1000] as const;
export const DEFAULT_ROWS = 50;
/** What a build may ask for before the quota says the account's max. */
export const BASE_MAX_ROWS = 200;

export const rowChoices = (maxRows: number | null | undefined): number[] => {
  const max = maxRows ?? BASE_MAX_ROWS;
  return ROW_CHOICES.filter((n) => n <= max);
};

/** The choice kept when the max changes (another account, the quota loading): the largest allowed at or under it. */
export const allowedRows = (rows: number, maxRows: number | null | undefined): number => {
  const choices = rowChoices(maxRows);
  return choices.filter((n) => n <= rows).at(-1) ?? choices[0] ?? DEFAULT_ROWS;
};

type Store = Pick<Storage, "getItem" | "setItem">;

export const autoApproveKey = (userId: string | null | undefined) => `paperclip.datasets.autoApprove.${userId ?? "anonymous"}`;

const browserStore = (): Store | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

export const readAutoApprove = (userId: string | null | undefined, store: Store | null = browserStore()): boolean => {
  try {
    return store?.getItem(autoApproveKey(userId)) === "1";
  } catch {
    return false;
  }
};

export const writeAutoApprove = (userId: string | null | undefined, on: boolean, store: Store | null = browserStore()) => {
  try {
    store?.setItem(autoApproveKey(userId), on ? "1" : "0");
  } catch {
    // Private mode or a full store: the choice lasts for this page only.
  }
};

/** The user's Auto-approve choice, remembered per user in this browser. */
export const useAutoApprove = (userId: string | null | undefined): [boolean, (on: boolean) => void] => {
  const [on, setOn] = useState(() => readAutoApprove(userId));
  useEffect(() => setOn(readAutoApprove(userId)), [userId]);
  return [
    on,
    (next: boolean) => {
      setOn(next);
      writeAutoApprove(userId, next);
    },
  ];
};

/** Next to Build: how many rows to build, and whether the proposed columns are approved without review. */
export const BuildOptions = ({
  rows,
  maxRows,
  onRows,
  autoApprove,
  onAutoApprove,
  disabled = false,
}: {
  rows: number;
  maxRows: number | null | undefined;
  onRows: (rows: number) => void;
  autoApprove: boolean;
  onAutoApprove: (on: boolean) => void;
  disabled?: boolean;
}) => (
  <>
    <label className="flex items-center gap-1.5" title="How many rows the build looks for. More rows take longer.">
      <span>Rows</span>
      <select
        aria-label="Rows"
        value={allowedRows(rows, maxRows)}
        disabled={disabled}
        onChange={(e) => onRows(Number(e.target.value))}
        className="rounded-[3px] border border-zinc-200 bg-white px-1 py-0.5 text-xs text-zinc-700 tabular-nums focus:border-zinc-400 focus:outline-none"
      >
        {rowChoices(maxRows).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </label>
    <label
      className="flex cursor-pointer items-center gap-1.5"
      title="Build with the proposed columns at once, without reviewing them first."
    >
      <input
        type="checkbox"
        checked={autoApprove}
        disabled={disabled}
        onChange={(e) => onAutoApprove(e.target.checked)}
        className="h-3.5 w-3.5 accent-zinc-900"
      />
      <span>Auto-approve columns</span>
    </label>
  </>
);
